import React, { useState, useMemo, useRef } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Rating,
  TextField,
  Alert,
  Skeleton,
  FormControlLabel,
  RadioGroup,
  Radio,
  FormControl,
  Slider,
} from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import { EMOJI_SETS, GLASS_SX } from './surveyHelpers';

// ─── ImageLightbox ────────────────────────────────────────────────────────────
const ZOOM_SCALE = 2.5;

const ImageLightbox = React.memo(({ url, onClose }) => {
  const [scale, setScale] = useState(1);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const dragRef = useRef({ dragging: false, startX: 0, startY: 0, origX: 0, origY: 0 });
  const lastTapRef = useRef(0);

  const toggleZoom = (clientX, clientY, containerRect) => {
    if (scale === 1) {
      // Zoom centrado aprox. en el punto tocado
      const offsetX = containerRect ? (containerRect.width / 2 - (clientX - containerRect.left)) : 0;
      const offsetY = containerRect ? (containerRect.height / 2 - (clientY - containerRect.top)) : 0;
      setScale(ZOOM_SCALE);
      setPos({ x: offsetX * (ZOOM_SCALE - 1) / ZOOM_SCALE, y: offsetY * (ZOOM_SCALE - 1) / ZOOM_SCALE });
    } else {
      setScale(1);
      setPos({ x: 0, y: 0 });
    }
  };

  // IMPORTANTE: stopPropagation acá. Sin esto, el primer click en la imagen
  // burbujea hasta el backdrop y, como scale === 1 en ese momento,
  // handleBackdropClick cierra el lightbox de inmediato — el doble click
  // nunca llega a registrarse porque el modal ya se cerró en el primer tap.
  const handleClick = (e) => {
    e.stopPropagation();
    const now = Date.now();
    const rect = e.currentTarget.getBoundingClientRect();
    if (now - lastTapRef.current < 300) {
      // doble tap/click
      toggleZoom(e.clientX, e.clientY, rect);
      lastTapRef.current = 0;
    } else {
      lastTapRef.current = now;
    }
  };

  const handleBackdropClick = () => {
    if (scale === 1) onClose();
  };

  const handlePointerDown = (e) => {
    if (scale === 1) return;
    dragRef.current = {
      dragging: true,
      startX: e.clientX,
      startY: e.clientY,
      origX: pos.x,
      origY: pos.y,
    };
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const handlePointerMove = (e) => {
    if (!dragRef.current.dragging) return;
    const dx = e.clientX - dragRef.current.startX;
    const dy = e.clientY - dragRef.current.startY;
    setPos({ x: dragRef.current.origX + dx, y: dragRef.current.origY + dy });
  };

  const handlePointerUp = () => {
    dragRef.current.dragging = false;
  };

  return (
    <Box
      onClick={handleBackdropClick}
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: 1400,
        background: 'rgba(0,0,0,0.92)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        touchAction: 'none',
      }}
    >
      <Box
        component="button"
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        aria-label="Cerrar"
        sx={{
          position: 'absolute',
          top: 16,
          right: 16,
          width: 40,
          height: 40,
          borderRadius: '50%',
          border: 'none',
          background: 'rgba(255,255,255,0.15)',
          color: '#fff',
          fontSize: 22,
          lineHeight: 1,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1,
        }}
      >
        ✕
      </Box>

      {scale === 1 && (
        <Typography
          sx={{
            position: 'absolute',
            bottom: 24,
            left: '50%',
            transform: 'translateX(-50%)',
            color: 'rgba(255,255,255,0.7)',
            fontSize: '0.8rem',
            pointerEvents: 'none',
          }}
        >
          Doble toque para ampliar
        </Typography>
      )}

      <Box
        component="img"
        src={url}
        alt="imagen ampliada"
        onClick={handleClick}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        sx={{
          maxWidth: '92vw',
          maxHeight: '88vh',
          objectFit: 'contain',
          cursor: scale === 1 ? 'zoom-in' : 'grab',
          transform: `scale(${scale}) translate(${pos.x / scale}px, ${pos.y / scale}px)`,
          transition: dragRef.current.dragging ? 'none' : 'transform 0.2s ease',
          userSelect: 'none',
          WebkitUserSelect: 'none',
        }}
        draggable={false}
      />
    </Box>
  );
});
ImageLightbox.displayName = 'ImageLightbox';

// ─── QuestionImage ────────────────────────────────────────────────────────────
export const QuestionImage = React.memo(({ url }) => {
  const [loaded, setLoaded] = useState(false);
  const [error, setError]   = useState(false);
  const [zoomOpen, setZoomOpen] = useState(false);

  if (!url || error) return null;

  return (
    <>
      <Box
        sx={{
          display:        'flex',
          justifyContent: 'center',
          mb:             2,
          borderRadius:   2,
          overflow:       'hidden',
        }}
      >
        {!loaded && (
          <Skeleton variant="rounded" width="100%" height={180} sx={{ maxWidth: 360 }} />
        )}
        <Box
          component="img"
          src={url}
          alt="question image"
          onLoad={() => setLoaded(true)}
          onError={() => setError(true)}
          onClick={() => loaded && setZoomOpen(true)}
          sx={{
            display:      loaded ? 'block' : 'none',
            maxWidth:     '100%',
            // Antes: maxHeight fijo en 300px, sin relación con el alto real
            // del viewport. En el flujo stepped (type 1) eso era lo primero
            // en empujar la tarjeta por encima del espacio disponible y
            // disparar el scroll interno. Con dvh en el clamp, la imagen se
            // reduce junto con el resto de la pregunta cuando el viewport es
            // bajo (landscape mobile, teclado abierto, etc.), y sigue
            // teniendo un tope razonable (280px) en pantallas altas.
            maxHeight:    'clamp(110px, 26dvh, 280px)',
            width:        'auto',
            height:       'auto',
            objectFit:    'contain',
            borderRadius: 2,
            boxShadow:    '0 2px 12px rgba(0,0,0,0.10)',
            cursor:       'zoom-in',
          }}
        />
      </Box>

      {zoomOpen && (
        <ImageLightbox url={url} onClose={() => setZoomOpen(false)} />
      )}
    </>
  );
});
QuestionImage.displayName = 'QuestionImage';

// ─── QuestionCard ─────────────────────────────────────────────────────────────
const QuestionCard = React.memo(({
  qId,
  question,
  answer,
  questionNumber,
  onRatingChange,
  onSliderChange,
  onSliderCommit,
  onTextChange,
  onRadioChange,
  onEmojiChange,
  showError,
  t,
}) => {
  // Se calcula una sola vez por instancia de pregunta (question.type no
  // cambia para un qId dado), evitando reconstruir el arreglo de emojis en
  // cada render (ej. cada vez que cambia `answer` al seleccionar uno).
  // `emojiNumber` también se precalcula acá (antes salía de un regex.match
  // corrido de nuevo por cada emoji en cada render de renderContent).
  const emojiEntries = useMemo(() => {
    const emojiSet = EMOJI_SETS[question.type];
    if (!emojiSet) return null;
    return Object.entries(emojiSet)
      .map(([key, value]) => [key, value, key.match(/face(\d+)/)?.[1] ?? key])
      .reverse();
  }, [question.type]);

  const renderContent = () => {
    switch (question.type) {

      case 'rating':
        return (
          <Box className="rating-container">
            <Rating
              name={`rating-${qId}`}
              value={answer || 0}
              onChange={(_, v) => onRatingChange(qId, v)}
              precision={1}
              size="large"
              emptyIcon={<StarIcon className="star-icon" />}
              className="rating"
            />
            <Typography variant="body2" color="text.secondary" className="rating-text">
              {answer === 0 ? t.notRated : t.ratingOf(answer)}
            </Typography>
          </Box>
        );

      case 'slider': {
        const cfg   = question.sliderConfig || {};
        const min   = cfg.min   ?? 0;
        const max   = cfg.max   ?? 100;
        const step  = cfg.step  ?? 1;
        const marks = cfg.marks ?? [
          { value: min, label: String(min) },
          { value: max, label: String(max) },
        ];
        return (
          <Box className="slider-container">
            <Slider
              value={answer ?? min}
              min={min}
              max={max}
              step={step}
              marks={marks}
              valueLabelDisplay="on"
              onChange={(_, v) => onSliderChange(qId, v)}
              onChangeCommitted={() => onSliderCommit?.(qId)}
              className="slider"
            />
            {cfg.labels && (
              <Box className="slider-labels">
                <Typography variant="caption" color="text.secondary">
                  {cfg.labels.min || ''}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {cfg.labels.max || ''}
                </Typography>
              </Box>
            )}
          </Box>
        );
      }

      case 'text':
        return (
          <TextField
            fullWidth
            multiline={question.multiline}
            rows={question.rows || 3}
            variant="outlined"
            placeholder={question.placeholder || t.textPlaceholder}
            value={answer || ''}
            onChange={(e) => onTextChange(qId, e)}
            className="text-field"
          />
        );

      case 'radio':
        return (
          <FormControl component="fieldset" className="radio-group">
            <RadioGroup
              value={answer || ''}
              onChange={(e) => onRadioChange(qId, e)}
              className="radio-group-row"
            >
              {question.options?.map((option, idx) => (
                <FormControlLabel
                  key={option.id?.toString() ?? idx.toString()}
                  value={option.id?.toString() ?? idx.toString()}
                  control={<Radio />}
                  label={option.text || ''}
                  className="radio-option"
                />
              ))}
            </RadioGroup>
          </FormControl>
        );

      case 'emojis':
      case 'emojis2':
      case 'emojisCat':
      case 'emojispeople':
      case 'emojislike':
      case 'emojisnumbers': {
        return (
          <Box className="emoji-container">
            <Box
              className="emoji-list"
              sx={{ gap: { xs: 0.5, sm: 1.5 } }}
              style={{
                display: 'flex',
                flexWrap: 'nowrap',
                alignItems: 'flex-end',
                justifyContent: 'center',
                width: '100%',
              }}
            >
              {emojiEntries.map(([key, value, emojiNumber]) => {
                const isSelected = answer === emojiNumber;
                return (
                  <Box
                    key={key}
                    onClick={() => onEmojiChange(qId, key)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onEmojiChange(qId, key);
                      }
                    }}
                    role="button"
                    tabIndex={0}
                    aria-pressed={isSelected}
                    aria-label={key}
                    className={`emoji-item${isSelected ? ' selected' : ''}`}
                    style={{
                      flex: '1 1 0',
                      minWidth: 0,
                      display: 'flex',
                      justifyContent: 'center',
                      cursor: 'pointer',
                    }}
                  >
                    <Box
                      component="img"
                      src={value}
                      alt={key}
                      className="emoji-img"
                      style={{
                        // Se agrega el término dvh (min(13vw, 14dvh)) para
                        // que los emojis también se achiquen en viewports
                        // bajos y no sean, junto con la imagen de pregunta,
                        // otro punto que empuje el alto total por encima
                        // del espacio disponible en el flujo stepped.
                        width: 'clamp(34px, min(13vw, 14dvh), 82px)',
                        height: 'clamp(34px, min(13vw, 14dvh), 82px)',
                        display: 'block',
                        objectFit: 'contain',
                        transform: isSelected ? 'scale(1.12)' : 'scale(1)',
                        transition: 'transform 0.15s ease',
                      }}
                    />
                  </Box>
                );
              })}
            </Box>
            {question.config?.show !== false && (
              <Box className="emoji-labels">
                <Typography variant="caption" color="text.secondary" className="emoji-label">
                  {question.config?.left || t.emojiLeft}
                </Typography>
                <Typography variant="caption" color="text.secondary" className="emoji-label">
                  {question.config?.right || t.emojiRight}
                </Typography>
              </Box>
            )}
          </Box>
        );
      }

      default:
        return (
          <Typography color="error">
            {t.unsupportedType
              ? t.unsupportedType(question.type)
              : `Question type '${question.type}' not supported`}
          </Typography>
        );
    }
  };

  return (
    <Card
      variant="outlined"
      className="question-card"
      sx={{
        ...GLASS_SX,
        ...(showError && {
          borderColor: 'error.main',
          borderWidth:  2,
          background:  'rgba(211,47,47,0.08) !important',
        }),
      }}
    >
      <CardContent>
        <QuestionImage url={question.url} />

        <Typography
          variant="h6"
          className={`question-text${question.type === 'radio' ? ' question-text-radio' : ''}`}
        >
          <Typography component="span" className="question-number">
            {questionNumber}
          </Typography>
          {question.text}
          {question.required && (
            <Typography component="span" color="error" className="required-asterisk">
              *
            </Typography>
          )}
        </Typography>

        {showError && (
          <Alert severity="error" sx={{ mt: 1, mb: 2 }}>
            {t.questionRequired}
          </Alert>
        )}

        {renderContent()}
      </CardContent>
    </Card>
  );
});
QuestionCard.displayName = 'QuestionCard';

export default QuestionCard;